import { IBroadcastFormRecipientResponseReportRow } from 'types/BroadcastForm'

/**
 * Maps option text to a sentiment value in the range 0.0 to 1.0
 * - 0.0 = Most negative
 * - 0.5 = Neutral
 * - 1.0 = Most positive
 * - null = Not applicable (e.g., multi-select options without clear sentiment)
 *
 * @param row - The response row containing OptionText and other metadata
 * @returns A sentiment value between 0.0 and 1.0, or null if not applicable
 */
export const computeSentiment = (
  row: IBroadcastFormRecipientResponseReportRow,
): number | null => {
  const optionText = row.OptionText?.trim() || ''
  const optionValue = row.OptionValue
  const questionCategory = row.QuestionCategory || ''

  // If we have OptionValue and it's a Net Promoter question, use the existing logic
  if (questionCategory === 'Net Promoter' && optionValue != null) {
    // Map NPS 0-10 scale to 0.0-1.0
    return optionValue / 10
  }

  // If we have OptionValue but no OptionText, try to infer from value
  if (optionValue != null && !optionText) {
    // For numeric scales, assume higher is better (can be overridden by specific mappings)
    // This is a fallback - specific OptionText mappings take precedence
    return null
  }

  // Create a normalized key for case-insensitive matching
  const normalizedText = optionText.toLowerCase()

  // Comprehensive mapping of option text to sentiment values
  const sentimentMap: Record<string, number | null> = {
    // Satisfaction scales (1-5)
    'very dissatisfied': 0.0,
    dissatisfied: 0.25,
    neutral: 0.5,
    satisfied: 0.75,
    'very satisfied': 1.0,

    // Good/Bad scales
    'very bad': 0.0,
    bad: 0.25,
    'very good': 1.0,
    good: 0.75,

    // Agree/Disagree scales
    'strongly disagree': 0.0,
    disagree: 0.25,
    'neither agree nor disagree': 0.5,
    'neither agree nor disagree (neutral)': 0.5,
    agree: 0.75,
    'strongly agree': 1.0,

    // Yes/No
    yes: 1.0,
    no: 0.0,

    // Yes/No/Unsure and Yes/Maybe/No
    unsure: 0.5,
    maybe: 0.5,

    // Weaker/Stronger
    weaker: 0.0,
    'somewhat weaker': 0.25,
    'the same': 0.5,
    'somewhat stronger': 0.75,
    'much stronger': 1.0,

    // Worse/Better
    worse: 0.0,
    'somewhat worse': 0.25,
    'somewhat better': 0.75,
    'much better': 1.0,

    // Importance
    'not important at all': 0.0,
    'not very important': 0.25,
    'somewhat important': 0.75,
    'very important': 1.0,

    // Poor/Excellent
    poor: 0.0,
    average: 0.5,
    excellent: 1.0,

    // A Lot/Not at All
    'a lot': 1.0,
    somewhat: 0.5,
    'not at all': 0.0,

    // Very Well/Not at All
    'very well': 1.0,
    'not much': 0.25,

    // Very Aware/Not at All Aware
    'very aware': 1.0,
    'somewhat aware': 0.5,
    'not very aware': 0.25,
    'not at all aware': 0.0,

    // Very Interested/Not Interested
    'very interested': 1.0,
    'somewhat interested': 0.5,
    'not sure': 0.5,
    'not interested': 0.0,

    // Very Comfortable/Uncomfortable
    'very comfortable': 1.0,
    'somewhat comfortable': 0.75,
    uncomfortable: 0.25,

    // Frequently/Never
    frequently: 1.0,
    occasionally: 0.5,
    rarely: 0.25,
    never: 0.0,

    // Very Likely/Not Likely
    'very likely': 1.0,
    likely: 0.75,
    'not likely': 0.25,

    // Daily/Never
    daily: 1.0,
    'several times a week': 0.8,

    // Very Easy/Very Difficult
    'very easy': 1.0,
    'somewhat easy': 0.75,
    easy: 0.75,
    difficult: 0.25,
    'very difficult': 0.0,

    // Very Likely/Very Unlikely
    'very unlikely': 0.0,
    unlikely: 0.25,

    // Neither easy nor difficult
    'neither easy nor difficult': 0.5,

    // Numeric scales (when OptionText is just a number)
    // eslint-disable-next-line quote-props
    '1': 0.0,
    // eslint-disable-next-line quote-props
    '2': 0.25,
    // eslint-disable-next-line quote-props
    '3': 0.5,
    // eslint-disable-next-line quote-props
    '4': 0.75,
    // eslint-disable-next-line quote-props
    '5': 1.0,
    // eslint-disable-next-line quote-props
    '6': 1.0, // For 6-point scales
    // eslint-disable-next-line quote-props
    '7': 1.0, // For 7-point scales
    // eslint-disable-next-line quote-props
    '8': 1.0, // For 8-point scales
    // eslint-disable-next-line quote-props
    '9': 1.0, // For 9-point scales
    // eslint-disable-next-line quote-props
    '10': 1.0, // For 10-point scales

    // Net Promoter ranges
    // eslint-disable-next-line quote-props
    '0': 0.0,
    '1-2': 0.15,
    '3-5': 0.4,
    '6-9': 0.75,
    '10 or more': 1.0,
    '6 or more': 1.0,
    'prefer not to say': null,
    'n/a': null,
  }

  // Check for exact match first
  if (sentimentMap[normalizedText] !== undefined) {
    const value = sentimentMap[normalizedText]
    if (value === null) {
      return null
    }
    return value
  }

  // If we have OptionValue but couldn't map it, return null
  // This is safer than guessing
  return null
}

/**
 * Determines if a sentiment value represents a positive, negative, or neutral response
 * @param sentiment - The sentiment value (0.0 to 1.0) or null
 * @returns 'Positive', 'Neutral', 'Negative', or null
 */
export const getSentimentLabel = (sentiment: number | null): string | null => {
  if (sentiment === null) {
    return null
  }

  if (sentiment >= 0.7) {
    return 'Positive'
  }
  if (sentiment <= 0.3) {
    return 'Negative'
  }
  return 'Neutral'
}
